import {useQuery, UseQueryResult} from '@tanstack/react-query';
import {z as r} from 'zod';

import {fetchAndDeserialize} from '../../util/api-adapter';
import {FetchMethodEnum, fetchX} from '../../util/fetch';
import {objectToUrlParameters} from '../../util/url';
import {getCsrfHeaders, mainApiHeaders, rootApiUrl} from '../api/api-const';

enum ReviewSuggestionActionEnum {
    apply = 'apply',
    dismiss = 'dismiss',
}

const reviewSuggestionItemSchema = r.object({
    id: r.number(),
    ruleId: r.number(),
    templateid: r.number(),
    reviewId: r.number(),
    comment: r.string().nullable(),
    gptComment: r.string().nullable(),
});

const reviewSuggestionResponseSchema = r.object({
    results: r.array(reviewSuggestionItemSchema),
});

export type ReviewSuggestionType = r.infer<typeof reviewSuggestionItemSchema>;

type ReviewSuggestionArrayType = r.infer<typeof reviewSuggestionResponseSchema>;

function getReviewsSuggestionsUrl(reviewIds: Array<number>) {
    return `/v4/reviews/suggestions/?${objectToUrlParameters({reviews_ids: reviewIds})}`;
}

function fetchReviewsSuggestions(reviewIds: Array<number>): Promise<ReviewSuggestionArrayType> {
    return fetchAndDeserialize<ReviewSuggestionArrayType>(
        getReviewsSuggestionsUrl(reviewIds),
        reviewSuggestionResponseSchema
    );
}

export function useReviewsSuggestions(reviewIds: Array<number>): UseQueryResult<ReviewSuggestionArrayType> {
    return useQuery([getReviewsSuggestionsUrl(reviewIds)], () => fetchReviewsSuggestions(reviewIds), {
        refetchOnWindowFocus: false,
    });
}

function fetchReviewSuggestionEdit(suggestionId: number, action: ReviewSuggestionActionEnum) {
    const payload = {action};

    return fetchX<void>(`${rootApiUrl}/v1/reviews/suggestions/${suggestionId}/`, {
        method: FetchMethodEnum.put,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body: JSON.stringify(payload),
    });
}

export function fetchReviewSuggestionApply(suggestionId: number): Promise<void> {
    return fetchReviewSuggestionEdit(suggestionId, ReviewSuggestionActionEnum.apply);
}

export function fetchReviewSuggestionDismiss(suggestionId: number): Promise<void> {
    return fetchReviewSuggestionEdit(suggestionId, ReviewSuggestionActionEnum.dismiss);
}
